import { Component, ErrorInfo, ReactNode } from "react";
import { Alert, AlertTitle, Box, Button } from "@mui/material";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Catches render errors anywhere in the routed views so a bug shows a message
// and a reload button instead of a blank white page. Errors in event handlers
// and promises are not caught here; React only routes render errors this way.
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Box sx={{ m: 3 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Reload
              </Button>
            }
          >
            <AlertTitle>Something went wrong</AlertTitle>
            The page hit an error and could not be displayed. Reloading usually fixes it. If it keeps
            happening, please report what you were doing along with this message:
            <Box component="pre" sx={{ mt: 1, whiteSpace: "pre-wrap", fontSize: "0.85em" }}>
              {this.state.error.message}
            </Box>
          </Alert>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
